/**
 * 프로덕트 API 응답 정규화 (중첩 data/result, list/prices 별칭, id 별칭)
 */

import type {
	GetProductDetailResponseDto,
	GetProductListResponseDto,
	GetProductPriceListResponseDto,
	ProductItemDetailDto,
	ProductItemType,
	ProductListItemDto,
	ProductPriceCurrency,
	ProductPriceDetailDto,
	ProductPriceListItemDto,
	ProductPricePurchaseType,
	ProductType,
} from '@/types/api/product';

function pickFiniteNumber(val: unknown): number | undefined {
	if (typeof val === 'number' && Number.isFinite(val)) return val;
	if (typeof val === 'string' && val.trim()) {
		const n = Number(String(val).replace(/,/g, '').trim());
		if (Number.isFinite(n)) return n;
	}
	return undefined;
}

function pickTrimmedString(...values: unknown[]): string {
	for (const value of values) {
		if (typeof value === 'string' && value.trim()) return value.trim();
		if (typeof value === 'number' && Number.isFinite(value)) return String(value);
	}
	return '';
}

/** 백엔드·스웨거 외 변형 문자열 → 프론트 `ProductType` */
export function normalizeApiProductType(raw: unknown): ProductType | undefined {
	if (raw == null) return undefined;
	const s = String(raw).trim();
	if (!s) return undefined;
	const compact = s.replace(/[\s_-]/g, '').toUpperCase();
	if (compact === 'CONTENT' || compact === 'CONTENTS') return 'CONTENT';
	if (compact === 'OPENCLASS' || compact === 'OPEN') return 'OPEN_CLASS';
	if (
		compact === 'ORGANIZATIONCLASS' ||
		compact === 'ORGCLASS' ||
		compact === 'ORGANIZATION'
	) {
		return 'ORGANIZATION_CLASS';
	}
	if (compact === 'EQUIPMENT') return 'EQUIPMENT';
	if (compact === 'CUSTOMSERVICE' || compact === 'CUSTOM') return 'CUSTOM_SERVICE';
	return undefined;
}

/**
 * GET /product/organization/purchased 등 응답의 판매유형 문자열 → enum
 * (per_device, Per Device, PER-DEVICE …)
 */
export function coercePurchaseTypeEnum(raw: unknown): ProductPricePurchaseType | undefined {
	if (raw == null) return undefined;
	if (typeof raw !== 'string') return undefined;
	const s = raw.trim();
	if (!s) return undefined;
	const u = s.toUpperCase().replace(/-/g, '_').replace(/\s+/g, '_');
	if (u === 'NONE') return 'NONE';
	if (u === 'PER_DEVICE') return 'PER_DEVICE';
	if (u === 'PER_USER') return 'PER_USER';
	if (u === 'PER_LICENSE') return 'PER_LICENSE';
	if (u === 'PER_ITEM') return 'PER_ITEM';
	const compact = u.replace(/_/g, '');
	if (compact === 'PERDEVICE') return 'PER_DEVICE';
	if (compact === 'PERUSER') return 'PER_USER';
	if (compact === 'PERLICENSE') return 'PER_LICENSE';
	if (compact === 'PERITEM') return 'PER_ITEM';
	return undefined;
}

/**
 * GET /product/{id} items 원소 — 백엔드가 `itemType`을 생략하거나 `type`만 줄 수 있음.
 * 강의를 시나리오로 오인하면 저장 시 scenarioId:0 등으로 나가 items 가 비정상 전송됨.
 */
function coerceProductItemDetailDto(raw: unknown): ProductItemDetailDto {
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
		return raw as ProductItemDetailDto;
	}
	const o = raw as Record<string, unknown>;
	const base = { ...(o as unknown as ProductItemDetailDto) };

	const explicit = o.itemType;
	const exStr = explicit != null ? String(explicit).trim().toUpperCase() : '';

	let itemType: ProductItemType | undefined;
	if (exStr === 'LECTURE' || exStr === 'SCENARIO') {
		itemType = exStr as ProductItemType;
	}

	const scenarioId = o.scenarioId;
	const lectureId = o.lectureId;
	const lectureList = Array.isArray(o.lectureList) ? o.lectureList : undefined;
	const vid = o.lectureVideoUrl;
	const hasVideoUrl = typeof vid === 'string' && vid.trim().length > 0;

	if (!itemType) {
		if (lectureId != null) itemType = 'LECTURE';
		else if (lectureList && lectureList.length > 0) itemType = 'LECTURE';
		/** 동영상 URL이 있으면 강의 우선 — 잘못 붙은 scenarioId 에 눌리지 않음 */
		else if (hasVideoUrl) itemType = 'LECTURE';
		else if (scenarioId != null) itemType = 'SCENARIO';
		else itemType = 'SCENARIO';
	}

	if (itemType === 'SCENARIO' && scenarioId == null) {
		if (lectureId != null || (lectureList && lectureList.length > 0) || hasVideoUrl) {
			itemType = 'LECTURE';
		}
	}

	return {
		...base,
		itemType,
	};
}

/** 목록 행: 루트 + 중첩 product 등을 합쳐 이수조건·타입 등 추출 */
function mergeProductListRowForFields(o: Record<string, unknown>): Record<string, unknown> {
	const out: Record<string, unknown> = { ...o };
	for (const key of ['product', 'productDetail', 'productInfo', 'detail'] as const) {
		const inner = o[key];
		if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
			Object.assign(out, inner as Record<string, unknown>);
		}
	}
	return out;
}

function mergeNestedPayload(raw: unknown): Record<string, unknown> {
	if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
		return {};
	}
	const top = raw as Record<string, unknown>;
	let merged: Record<string, unknown> = { ...top };
	for (const k of ['data', 'result', 'payload'] as const) {
		const inner = top[k];
		if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
			merged = { ...merged, ...(inner as Record<string, unknown>) };
		}
	}
	return merged;
}

/** GET 상세: `data.product` 등으로 items/prices가 한 단계 더 감싸진 경우 평탄화 */
function flattenProductDetailWrapper(merged: Record<string, unknown>): Record<string, unknown> {
	let m: Record<string, unknown> = { ...merged };
	for (const key of ['product', 'productDetail', 'productInfo', 'detail'] as const) {
		const inner = m[key];
		if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
			m = { ...m, ...(inner as Record<string, unknown>) };
		}
	}
	return m;
}

export function normalizeGetProductListPayload(raw: unknown): GetProductListResponseDto {
	const merged = mergeNestedPayload(raw);
	const listRaw = merged.productList;
	const arr = Array.isArray(listRaw) ? listRaw : [];

	const productList: ProductListItemDto[] = arr.map((item) => {
		if (!item || typeof item !== 'object') return item as ProductListItemDto;
		const o = item as Record<string, unknown>;
		const idRaw = o.productId;
		const productId =
			typeof idRaw === 'number' && Number.isFinite(idRaw)
				? idRaw
				: 0;
		const typeNorm = (normalizeApiProductType(o.type) ?? o.type) as ProductType;
		const priceCountRaw = o.priceCount;
		const priceCount =
			typeof priceCountRaw === 'number' && Number.isFinite(priceCountRaw)
				? Math.floor(priceCountRaw)
				: undefined;
		const isActiveRaw = o.isActive;
		const isActive =
			isActiveRaw === false || isActiveRaw === 'false' || isActiveRaw === 0 ? false : true;
		return {
			...(o as unknown as ProductListItemDto),
			productId,
			type: typeNorm,
			...(priceCount !== undefined ? { priceCount } : {}),
			isActive,
		};
	});

	const totalRaw = merged.totalCount ?? merged.total ?? productList.length;
	const totalCount = Number(totalRaw);
	return {
		productList,
		totalCount: Number.isFinite(totalCount) ? totalCount : productList.length,
	};
}

/**
 * `GET /product/price/list` 행들을 `productId` 기준으로 합쳐 상품 관리 그리드용 `ProductListItemDto[]` 로 변환.
 * (백엔드가 프로덕트 목록을 price/list 로만 제공할 때)
 */
export function buildProductListFromPriceListRows(rows: Record<string, unknown>[]): ProductListItemDto[] {
	type Agg = { productId: number; title: string; type: ProductType; rows: Record<string, unknown>[] };
	const byPid = new Map<number, Agg>();

	for (const r of rows) {
		if (!r || typeof r !== 'object') continue;
		const idRaw = r.productId;
		const pid =
			typeof idRaw === 'number' && Number.isFinite(idRaw) && idRaw > 0
				? Math.floor(idRaw)
				: 0;
		if (!pid) continue;

		const title =
			String(r.productTitle).trim() || `프로덕트 #${pid}`;
		const typeNorm =
			normalizeApiProductType(r.productType) ?? ('CONTENT' as ProductType);

		const cur = byPid.get(pid);
		if (!cur) {
			byPid.set(pid, { productId: pid, title, type: typeNorm, rows: [r] });
		} else {
			cur.rows.push(r);
		}
	}

	const out: ProductListItemDto[] = [];
	for (const v of byPid.values()) {
		const merged: Record<string, unknown> = {};
		for (const row of v.rows) {
			Object.assign(merged, row);
		}
		const scenarioCount = 0;
		const lectureCount = 0;
		const anyActive = v.rows.some((row) => {
			const a = row.isActive;
			return a !== false && a !== 'false' && a !== 0;
		});
		const createdRaw = merged.createdAt;
		const updatedRaw = merged.updatedAt;
		const createdAt =
			typeof createdRaw === 'string' && createdRaw.trim()
				? createdRaw.trim()
				: new Date(0).toISOString();
		const updatedAt =
			typeof updatedRaw === 'string' && updatedRaw.trim()
				? updatedRaw.trim()
				: createdAt;
		const premiumRaw = merged.premiumTypes;
		const premiumTypes = Array.isArray(premiumRaw)
			? (premiumRaw.filter((x) => x && typeof x === 'object') as ProductListItemDto['premiumTypes'])
			: [];

		out.push({
			productId: v.productId,
			title: v.title,
			type: v.type,
			scenarioCount,
			lectureCount,
			priceCount: v.rows.length,
			isActive: anyActive,
			createdAt,
			updatedAt,
			premiumTypes,
		});
	}

	out.sort((a, b) => a.productId - b.productId);
	return out;
}

function normalizeProductPriceListRow(raw: unknown): ProductPriceListItemDto | null {
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
	const o = raw as Record<string, unknown>;

	const productPriceIdRaw = o.productPriceId;
	const productPriceId =
		typeof productPriceIdRaw === 'number' && Number.isFinite(productPriceIdRaw)
			? Math.trunc(productPriceIdRaw)
			: 0;

	const productIdRaw = o.productId;
	const productId =
		typeof productIdRaw === 'number' && Number.isFinite(productIdRaw)
			? Math.trunc(productIdRaw)
			: 0;

	const productType = normalizeApiProductType(o.productType) ?? 'CONTENT';
	const salesCount = pickFiniteNumber(o.salesCount) ?? 0;
	const unitPrice = pickFiniteNumber(o.unitPrice) ?? 0;
	const totalSalesAmount = pickFiniteNumber(o.totalSalesAmount) ?? 0;

	const currencyRaw = o.currency;
	const currency: ProductPriceCurrency = currencyRaw === 'USD' ? 'USD' : 'KRW';

	const countryIdRaw = o.countryId;
	const countryId =
		typeof countryIdRaw === 'number' && Number.isFinite(countryIdRaw)
			? Math.trunc(countryIdRaw)
			: undefined;
	const countryTitle = pickTrimmedString(o.countryTitle) || null;

	const partnerOrgIdRaw = o.partnerOrganizationId;
	const partnerOrganizationId =
		typeof partnerOrgIdRaw === 'number' && Number.isFinite(partnerOrgIdRaw)
			? Math.trunc(partnerOrgIdRaw)
			: undefined;
	const partnerOrganizationTitle = pickTrimmedString(o.partnerOrganizationTitle) || null;

	const costPrice = pickFiniteNumber(o.costPrice);

	const isActiveRaw = o.isActive;
	const isActive =
		isActiveRaw === false || isActiveRaw === 'false' || isActiveRaw === 0 ? false : true;

	const productIsActiveRaw = o.productIsActive;
	const productIsActiveExplicit =
		productIsActiveRaw === undefined || productIsActiveRaw === null
			? undefined
			: productIsActiveRaw === false || productIsActiveRaw === 'false' || productIsActiveRaw === 0
				? false
				: true;

	return {
		skuCode: pickTrimmedString(o.skuCode) ?? '',
		productPriceId,
		productType,
		productTitle: pickTrimmedString(o.productTitle) ?? '',
		productId,
		title: pickTrimmedString(o.title) ?? '',
		salesCount: Math.max(0, Math.floor(salesCount)),
		unitPrice,
		...(costPrice != null ? { costPrice } : {}),
		totalSalesAmount,
		currency,
		countryTitle,
		...(countryId != null ? { countryId } : {}),
		...(partnerOrganizationId != null ? { partnerOrganizationId } : {}),
		partnerOrganizationTitle,
		isActive,
		...(productIsActiveExplicit !== undefined ? { productIsActive: productIsActiveExplicit } : {}),
	};
}

export function normalizeGetProductPriceListPayload(raw: unknown): GetProductPriceListResponseDto {
	const merged = mergeNestedPayload(raw);
	const listRaw = merged.list ?? [];
	const list = (Array.isArray(listRaw) ? listRaw : [])
		.map(normalizeProductPriceListRow)
		.filter((row): row is ProductPriceListItemDto => row != null);

	const totalCount = pickFiniteNumber(merged.totalCount) ?? list.length;
	const page = pickFiniteNumber(merged.page) ?? 1;
	const pageSize = pickFiniteNumber(merged.pageSize) ?? list.length;
	const totalPages = pickFiniteNumber(merged.totalPages) ?? (pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1);

	return {
		list,
		totalCount: Math.max(0, Math.floor(totalCount)),
		page: Math.max(1, Math.floor(page)),
		pageSize: Math.max(1, Math.floor(pageSize || 1)),
		totalPages: Math.max(1, Math.floor(totalPages || 1)),
	};
}

export function normalizeGetProductDetailPayload(raw: unknown): GetProductDetailResponseDto {
	let merged = mergeNestedPayload(raw);
	merged = flattenProductDetailWrapper(merged);
	if (!Object.keys(merged).length) {
		return {
			type: 'CONTENT',
			title: '',
			items: [],
			isActive: true,
			createdAt: new Date().toISOString(),
			prices: [],
		} as GetProductDetailResponseDto;
	}

	const pricesRaw = merged.prices;
	const arr = Array.isArray(pricesRaw) ? pricesRaw : [];

	const coerceMoney = (val: unknown): number | undefined => {
		if (typeof val === 'number' && Number.isFinite(val) && val >= 0) return val;
		if (typeof val === 'string' && val.trim()) {
			const n = Number(val.replace(/,/g, '').trim());
			if (Number.isFinite(n) && n >= 0) return n;
		}
		return undefined;
	};

	const itemsRaw = merged.items;
	const items: ProductItemDetailDto[] = Array.isArray(itemsRaw)
		? itemsRaw.map(coerceProductItemDetailDto)
		: [];

	const partnerOrgIdRaw = merged.partnerOrganizationId;
	const partnerOrganizationId =
		typeof partnerOrgIdRaw === 'number' && Number.isFinite(partnerOrgIdRaw)
			? Math.trunc(partnerOrgIdRaw)
			: undefined;
	const partnerOrganizationTitle = pickTrimmedString(merged.partnerOrganizationTitle) || undefined;

	const prices: ProductPriceDetailDto[] = arr.map((item) => {
		if (!item || typeof item !== 'object') {
			return item as ProductPriceDetailDto;
		}
		const o = item as Record<string, unknown>;
		const idRaw = o.productPriceId;
		const productPriceId =
			typeof idRaw === 'number' && Number.isFinite(idRaw)
				? Math.trunc(idRaw)
				: 0;

		const regularPrice = coerceMoney(o.regularPrice) ?? 0;

		const discountedPrice = coerceMoney(o.discountedPrice);

		const countries = Array.isArray(o.countries) ? o.countries : [];
		const countryIdRaw = o.countryId;
		const countryId =
			typeof countryIdRaw === 'number' && Number.isFinite(countryIdRaw)
				? Math.trunc(countryIdRaw)
				: undefined;
		const countryTitle = pickTrimmedString(o.countryTitle) || undefined;
		const base = { ...o } as Record<string, unknown>;
		const title = typeof o.title === 'string' && o.title.trim() ? o.title.trim() : String(o.title ?? '');
		const skuCode = typeof o.skuCode === 'string' ? o.skuCode : String(o.skuCode ?? '');
		const currency = (o.currency as ProductPriceDetailDto['currency']) ?? 'KRW';
		const purchaseType = (o.purchaseType as ProductPriceDetailDto['purchaseType']) ?? 'PER_USER';
		const isActive = o.isActive !== false;

		return {
			...(base as unknown as ProductPriceDetailDto),
			productPriceId,
			skuCode,
			title,
			regularPrice,
			currency,
			purchaseType,
			isActive,
			...(discountedPrice != null ? { discountedPrice } : {}),
			...(countryId != null ? { countryId } : {}),
			...(countryTitle ? { countryTitle } : {}),
			countries: countries as ProductPriceDetailDto['countries'],
		};
	});

	const out: GetProductDetailResponseDto = {
		...(merged as unknown as GetProductDetailResponseDto),
		prices,
		items,
		...(partnerOrganizationId != null ? { partnerOrganizationId } : {}),
		...(partnerOrganizationTitle ? { partnerOrganizationTitle } : {}),
	};
	return out;
}
